import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Eye, Edit, Trash2, ArrowUpDown, ArrowUp, ArrowDown, MoreVertical } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

interface Post {
  id: number;
  title: string;
  slug: string;
  category: string;
  created_at: string;
  views: number;
  status: 'draft' | 'pending_review' | 'published';
  clicks?: number;
}

type SortField = 'title' | 'category' | 'created_at' | 'views' | 'clicks';
type SortDirection = 'asc' | 'desc';

interface PublishedPostsTableProps {
  onEdit: (post: Post) => void;
  onDelete: (id: number) => void;
  startDate?: Date | null;
  endDate?: Date | null;
}

export default function PublishedPostsTable({ 
  onEdit, 
  onDelete,
  startDate,
  endDate 
}: PublishedPostsTableProps) {
  const navigate = useNavigate();
  const [posts, setPosts] = useState<Post[]>([]);
  const [loading, setLoading] = useState(true);
  const [sortField, setSortField] = useState<SortField>('created_at');
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');

  useEffect(() => {
    loadPosts();
  }, [startDate, endDate]);

  const loadPosts = async () => {
    try {
      setLoading(true);
      
      let query = supabase
        .from("posts")
        .select("id, title, slug, category, created_at, views, status")
        .eq("status", "published");

      if (startDate) {
        query = query.gte("created_at", startDate.toISOString());
      }
      if (endDate) {
        const endOfDay = new Date(endDate);
        endOfDay.setHours(23, 59, 59, 999);
        query = query.lte("created_at", endOfDay.toISOString());
      }

      const { data: postsData, error: postsError } = await query;
      if (postsError) throw postsError;

      const postsWithClicks = await Promise.all(
        (postsData || []).map(async (post) => {
          let clickQuery = supabase
            .from("kopfai_clicks")
            .select("id", { count: "exact", head: true })
            .eq("post_id", post.id);

          if (startDate) {
            clickQuery = clickQuery.gte("clicked_at", startDate.toISOString());
          }
          if (endDate) {
            const endOfDay = new Date(endDate);
            endOfDay.setHours(23, 59, 59, 999);
            clickQuery = clickQuery.lte("clicked_at", endOfDay.toISOString());
          }

          const { count } = await clickQuery;
          return { ...post, clicks: count || 0 };
        })
      );

      setPosts(postsWithClicks);
    } catch (error) {
      console.error("Error loading posts:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('desc');
    }
  };

  const getSortedPosts = () => {
    return [...posts].sort((a, b) => {
      let aValue: any = a[sortField];
      let bValue: any = b[sortField];

      if (typeof aValue === 'string') {
        aValue = aValue.toLowerCase();
        bValue = bValue.toLowerCase();
      }

      if (sortDirection === 'asc') {
        return aValue > bValue ? 1 : -1;
      } else {
        return aValue < bValue ? 1 : -1;
      }
    });
  };

  const SortIcon = ({ field }: { field: SortField }) => {
    if (sortField !== field) {
      return <ArrowUpDown className="ml-1 h-3 w-3 text-muted-foreground" />;
    }
    return sortDirection === 'asc' ? (
      <ArrowUp className="ml-1 h-3 w-3" />
    ) : (
      <ArrowDown className="ml-1 h-3 w-3" />
    );
  };

  if (loading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Posts Publicados</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex justify-center py-8">
            <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
          </div>
        </CardContent>
      </Card>
    );
  }

  const sortedPosts = getSortedPosts();

  const MobilePostCard = ({ post }: { post: Post }) => (
    <div className="p-4 border rounded-lg space-y-3">
      <div className="flex justify-between items-start gap-2">
        <h3 className="font-medium text-sm line-clamp-2 flex-1">{post.title}</h3>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
              <MoreVertical className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="bg-popover">
            <DropdownMenuItem onClick={() => navigate(`/post/${post.slug}`)}>
              <Eye className="h-4 w-4 mr-2" />
              Ver post
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => onEdit(post)}>
              <Edit className="h-4 w-4 mr-2" />
              Editar
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => onDelete(post.id)} className="text-destructive">
              <Trash2 className="h-4 w-4 mr-2" />
              Excluir
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
      <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted-foreground">
        <span className="font-medium text-foreground">{post.category}</span>
        <span>{new Date(post.created_at).toLocaleDateString('pt-BR')}</span>
      </div>
      <div className="flex gap-4 text-xs">
        <span><strong>{post.views}</strong> views</span>
        <span className="text-kopf-accent"><strong>{post.clicks}</strong> clicks</span>
      </div>
    </div>
  );

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg sm:text-xl">Posts Publicados ({sortedPosts.length})</CardTitle>
      </CardHeader>
      <CardContent>
        {/* Mobile view */}
        <div className="md:hidden space-y-3">
          {sortedPosts.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              Nenhum post publicado no período selecionado.
            </div>
          ) : (
            sortedPosts.map((post) => <MobilePostCard key={post.id} post={post} />)
          )}
        </div>

        {/* Desktop table view */}
        <div className="hidden md:block overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b">
                <th className="text-left p-3">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleSort('title')}
                    className="font-semibold text-xs"
                  >
                    Título
                    <SortIcon field="title" />
                  </Button>
                </th>
                <th className="text-left p-3">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleSort('category')}
                    className="font-semibold text-xs"
                  >
                    Categoria
                    <SortIcon field="category" />
                  </Button>
                </th>
                <th className="text-left p-3">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleSort('created_at')}
                    className="font-semibold text-xs"
                  >
                    Data
                    <SortIcon field="created_at" />
                  </Button>
                </th>
                <th className="text-left p-3">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleSort('views')}
                    className="font-semibold text-xs"
                  >
                    Views
                    <SortIcon field="views" />
                  </Button>
                </th>
                <th className="text-left p-3">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleSort('clicks')}
                    className="font-semibold text-xs"
                  >
                    Clicks
                    <SortIcon field="clicks" />
                  </Button>
                </th>
                <th className="text-right p-3 text-xs">Ações</th>
              </tr>
            </thead>
            <tbody>
              {sortedPosts.map((post) => (
                <tr key={post.id} className="border-b hover:bg-accent/50 transition-colors">
                  <td className="p-3">
                    <div className="font-medium max-w-xs truncate text-sm">{post.title}</div>
                  </td>
                  <td className="p-3">
                    <span className="text-sm text-muted-foreground">{post.category}</span>
                  </td>
                  <td className="p-3">
                    <span className="text-sm">
                      {new Date(post.created_at).toLocaleDateString('pt-BR')}
                    </span>
                  </td>
                  <td className="p-3">
                    <span className="text-sm font-medium">{post.views}</span>
                  </td>
                  <td className="p-3">
                    <span className="text-sm font-medium text-kopf-accent">{post.clicks}</span>
                  </td>
                  <td className="p-3">
                    <div className="flex justify-end gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => navigate(`/post/${post.slug}`)}
                      >
                        <Eye className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => onEdit(post)}
                      >
                        <Edit className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => onDelete(post.id)}
                      >
                        <Trash2 className="h-4 w-4 text-destructive" />
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {sortedPosts.length === 0 && (
            <div className="text-center py-8 text-muted-foreground">
              Nenhum post publicado no período selecionado.
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}